import { createRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { BusinessAlertKindDto, BusinessAlertTargetDto } from "@carry/contract";
import { Route as RootRoute } from "./__root";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";
import { formatDateTime, jstToday } from "../lib/format";
import { QueryErrorBanner } from "../components/query-error-banner";
import { DeliveryTrendChart } from "../components/dashboard/delivery-trend-chart";
import { StatusPieChart } from "../components/dashboard/status-pie-chart";
import { DriverBarChart } from "../components/dashboard/driver-bar-chart";

export const DASHBOARD_REFETCH_INTERVAL_MS = 30_000;

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: DashboardPage,
});

function DashboardPage() {
  const today = jstToday();
  const {
    data: dashboardStats,
    error: statsError,
    refetch: refetchStats,
  } = useDashboardStats(today);
  // #641: アラートも同じ経路 (便区分の境界) を通るため、失敗を握り潰すと未読の配送指示や
  // 未提出日報が「0 件」に見える。stats と同じく理由を出す。
  const {
    data: dashboardAlerts,
    error: alertsError,
    refetch: refetchAlerts,
  } = useDashboardAlerts();

  const stats = dashboardStats?.data;
  const alerts = (dashboardAlerts?.data ?? []).slice(0, 5);
  const activeAlertCount = dashboardAlerts?.pagination?.total ?? 0;
  // 配送 KPI は期間を切り替えず、常に JST 当日 (dashboard.stats の orders) を表示する。
  const todayCount = stats?.orders.total ?? 0;
  const completed = stats?.orders.completed ?? 0;
  const remaining = stats?.orders.remaining ?? 0;
  const completionRate = stats?.orders.completion_rate ?? 0;

  const kpis: KpiItem[] = [
    {
      label: "本日 配送件数",
      value: String(todayCount),
      unit: "件",
      sub: `予定 ${todayCount} / 完了 ${completed} / 残り ${remaining}`,
    },
    {
      label: "完了率",
      value: completionRate.toFixed(1),
      unit: "%",
      sub: "本日の完了率",
    },
    {
      label: "ドライバー稼働",
      value: `${stats?.drivers.active ?? 0}/${stats?.drivers.total ?? 0}`,
      unit: "名",
      sub: "active / total",
    },
    {
      label: "未承認日報",
      value: String(stats?.pending_reports ?? 0),
      unit: "件",
      sub: "submitted",
    },
  ];

  return (
    <div style={{ margin: -24 }}>
      {/* #641: システム設定の未登録など運用で解消できる失敗は文言ごと出す。
          握り潰すと KPI・アラートが 0 件に見えて原因に辿り着けない。 */}
      {statsError && (
        <QueryErrorBanner label="集計" error={statsError} onRetry={() => void refetchStats()} />
      )}
      {alertsError && (
        <QueryErrorBanner
          label="業務アラート"
          error={alertsError}
          onRetry={() => void refetchAlerts()}
        />
      )}
      <KpiStrip kpis={kpis} />

      <SectionHeader
        title="Alerts"
        accent={
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--danger)" }}>
            {activeAlertCount} active
          </span>
        }
        viewAll={{ to: "/alerts", label: "アラート履歴" }}
      />
      <div
        style={{
          margin: "0 32px",
          background: "var(--bg-raised)",
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
        }}
      >
        {alerts.length === 0 && (
          <div style={{ padding: "18px", fontSize: 12, color: "var(--ink-3)" }}>
            現在のアラートはありません
          </div>
        )}
        {alerts.map((a, i) => (
          <div
            key={a.id}
            style={{
              display: "flex",
              gap: 16,
              padding: "14px 18px",
              borderBottom: i < alerts.length - 1 ? "0.5px solid var(--rule-soft)" : "none",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: ALERT_INK[a.kind],
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 180, fontSize: 12, fontWeight: 500 }}>{a.title}</div>
            <div style={{ flex: 1, fontSize: 12, color: "var(--ink-2)" }}>{a.body}</div>
            <div style={{ fontSize: 10, color: "var(--ink-3)", fontFamily: "var(--f-mono)" }}>
              {formatDateTime(a.occurred_at)}
            </div>
            <AlertActionButton target={a.target} />
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 1fr",
          gap: 20,
          margin: "20px 32px 0",
        }}
      >
        <DeliveryTrendChart values={stats?.daily_trend ?? []} />
        <StatusPieChart
          breakdown={
            stats?.status_breakdown ?? {
              draft: 0,
              confirmed: 0,
              assigned: 0,
              completed: 0,
              cancelled: 0,
            }
          }
        />
      </div>
      <div style={{ margin: "20px 32px 32px" }}>
        <DriverBarChart byDriver={stats?.by_driver ?? []} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

export function useDashboardStats(today: string) {
  const { session } = useAuth();
  const api = useAdminApi();
  return useQuery({
    queryKey: ["dashboard-stats", session?.user?.id, today],
    queryFn: () => api.dashboard.stats({ date: today }),
    enabled: !!session,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });
}

export function useDashboardAlerts() {
  const { session } = useAuth();
  const api = useAdminApi();
  return useQuery({
    queryKey: ["dashboard-alerts", session?.user?.id],
    queryFn: () => api.alert.list({ status: "active", limit: 5 }),
    enabled: !!session,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });
}

// ────────────────────────────────────────────────────────
// Sub components
// ────────────────────────────────────────────────────────

interface KpiItem {
  label: string;
  value: string;
  unit: string;
  sub: string;
  delta?: string;
}

function KpiStrip({ kpis }: { kpis: KpiItem[] }) {
  return (
    <div
      className="cp-summary-strip"
      style={{
        // grid 指定にしておくと、900px 以下では tokens.css が 2 列 (2×2) へ切り替える.
        display: "grid",
        gridTemplateColumns: `repeat(${kpis.length}, minmax(0, 1fr))`,
        margin: "20px 32px 0",
        background: "var(--bg-raised)",
        border: "0.5px solid var(--rule)",
        borderRadius: 3,
      }}
    >
      {kpis.map((k, i) => (
        <div
          key={k.label}
          style={{
            padding: "18px 20px",
            borderRight: i < kpis.length - 1 ? "0.5px solid var(--rule-soft)" : "none",
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            {k.label}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{ fontSize: "var(--fs-3xl)", fontWeight: 500, letterSpacing: "-.02em" }}
              className="num"
            >
              {k.value}
            </span>
            {k.unit && <span style={{ fontSize: 13, color: "var(--ink-3)" }}>{k.unit}</span>}
            {k.delta && (
              <span
                style={{
                  fontFamily: "var(--f-mono)",
                  fontSize: 10,
                  color: "var(--ok)",
                  marginLeft: 4,
                }}
              >
                {k.delta}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

const ALERT_INK: Record<BusinessAlertKindDto, string> = {
  distance_deviation_alert: "var(--warn)",
  unconfirmed_instruction: "var(--warn)",
  unsubmitted_report: "var(--warn)",
  alcohol_check_failed: "var(--danger)",
  vehicle_inspection_ng: "var(--danger)",
  license_expiration_alert: "var(--warn)",
  daily_report_rejected: "var(--warn)",
  delay_reported: "var(--warn)",
  unrecorded_arrival_odometer: "var(--warn)",
};

/** ダッシュボードのアラート行から対応先画面へ遷移するボタン. */
function AlertActionButton({ target }: { target: BusinessAlertTargetDto }) {
  if (!target) return null;

  const style = {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 10px",
    border: "0.5px solid var(--rule-soft)",
    borderRadius: 2,
    background: "var(--bg-raised)",
    color: "var(--ink)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  } as const;

  switch (target.kind) {
    case "distance":
      // #627: 距離突合を廃止したため、走行距離乖離は運転日報で確認する。
      return (
        <Link to="/reports" style={style}>
          運転日報へ
        </Link>
      );
    case "route":
      return (
        <Link to="/routes" search={{ date: target.date }} style={style}>
          配送指示へ
        </Link>
      );
    case "report":
      return (
        <Link to="/reports" style={style}>
          日報確認へ
        </Link>
      );
    case "order":
      return (
        <Link to="/orders/$publicId" params={{ publicId: target.order_public_id }} style={style}>
          対象注文へ
        </Link>
      );
  }
}

function SectionHeader({
  title,
  accent,
  viewAll,
}: {
  title: string;
  accent?: React.ReactNode;
  /** 「すべて表示」リンクの遷移先. 省略時はボタンを描画しない (誤期待を防ぐため). */
  viewAll?: { to: "/reports" | "/notifications" | "/alerts"; label: string };
}) {
  return (
    <div
      style={{
        padding: "20px 32px 8px",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: "-.005em",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span className="eyebrow">{title}</span>
        {accent}
      </h2>
      {viewAll && (
        <Link
          to={viewAll.to}
          style={{
            fontSize: 12,
            color: "var(--accent)",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          {viewAll.label} →
        </Link>
      )}
    </div>
  );
}
